import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSidebarStore } from '@/lib/stores';
import {
  LayoutDashboard, FolderKanban, BookTemplate, FileText,
  Settings, ChevronLeft, ChevronRight, Beaker, Shield,
  HelpCircle, X
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

const mainNav = [
  { path: '/app', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/app/projects', icon: FolderKanban, label: 'Projects' },
  { path: '/app/templates', icon: BookTemplate, label: 'Templates' },
  { path: '/app/guidances', icon: FileText, label: 'Guidances' },
];

const bottomNav = [
  { path: '/app/settings', icon: Settings, label: 'Settings' },
  { path: '/app/help', icon: HelpCircle, label: 'Help' },
];

const adminNav = [
  { path: '/admin', icon: Shield, label: 'Admin Panel' },
];

export function Sidebar({ userRole }) {
  const { collapsed, toggle, mobileOpen, closeMobile } = useSidebarStore();
  const location = useLocation();

  // Close mobile sidebar on navigation
  useEffect(() => {
    closeMobile();
  }, [location.pathname]);

  // Close on escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && mobileOpen) closeMobile();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mobileOpen]);

  const navContent = (isMobile = false) => (
    <>
      {/* Logo */}
      <div className="flex items-center h-14 px-4 gap-3">
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
          <Beaker className="w-4 h-4 text-primary-foreground" />
        </div>
        <AnimatePresence>
          {(isMobile || !collapsed) && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              className="font-display text-lg font-bold tracking-tight whitespace-nowrap overflow-hidden"
              style={{ fontFamily: 'Space Grotesk' }}
            >
              Pharmax.ai
            </motion.span>
          )}
        </AnimatePresence>
        {/* Close button — mobile only */}
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={closeMobile}
            className="ml-auto h-8 w-8 rounded-lg hover:bg-muted/50 lg:hidden"
            data-testid="sidebar-mobile-close"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      <Separator />

      {/* Main nav */}
      <nav className="flex-1 flex flex-col gap-1 px-2 py-3 overflow-y-auto">
        <TooltipProvider delayDuration={0}>
          {mainNav.map((item) => {
            const isActive = item.exact
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);
            const showLabel = isMobile || !collapsed;
            return (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={item.path}
                    data-testid={`app-sidebar-nav-${item.label.toLowerCase()}-link`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                      isActive
                        ? 'bg-primary/10 text-foreground border border-primary/20'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    } ${!showLabel ? 'justify-center' : ''}`}
                  >
                    <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                    <AnimatePresence>
                      {showLabel && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="whitespace-nowrap"
                        >
                          {item.label}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </NavLink>
                </TooltipTrigger>
                {!showLabel && (
                  <TooltipContent side="right">{item.label}</TooltipContent>
                )}
              </Tooltip>
            );
          })}
        </TooltipProvider>
      </nav>

      {/* Bottom nav */}
      <div className="flex flex-col gap-1 px-2 py-3 border-t border-border">
        <TooltipProvider delayDuration={0}>
          {userRole === 'superadmin' && adminNav.map((item) => (
            <Tooltip key={item.path}>
              <TooltipTrigger asChild>
                <NavLink
                  to={item.path}
                  data-testid="app-sidebar-nav-admin-link"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 ${(!isMobile && collapsed) ? 'justify-center' : ''}`}
                >
                  <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                  {(isMobile || !collapsed) && <span className="whitespace-nowrap">{item.label}</span>}
                </NavLink>
              </TooltipTrigger>
              {!isMobile && collapsed && <TooltipContent side="right">{item.label}</TooltipContent>}
            </Tooltip>
          ))}
          {bottomNav.map((item) => (
            <Tooltip key={item.path}>
              <TooltipTrigger asChild>
                <NavLink
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 ${(!isMobile && collapsed) ? 'justify-center' : ''}`}
                >
                  <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                  {(isMobile || !collapsed) && <span className="whitespace-nowrap">{item.label}</span>}
                </NavLink>
              </TooltipTrigger>
              {!isMobile && collapsed && <TooltipContent side="right">{item.label}</TooltipContent>}
            </Tooltip>
          ))}
        </TooltipProvider>

        {/* Collapse toggle — desktop only */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggle}
          data-testid="app-sidebar-toggle-button"
          className="mt-1 w-full h-9 hover:bg-muted/50 hidden lg:flex"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* ── Desktop sidebar (lg+) ── */}
      <motion.aside
        data-testid="app-sidebar"
        initial={false}
        animate={{ width: collapsed ? 64 : 260 }}
        transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
        className="h-screen flex-col border-r border-border bg-background/80 backdrop-blur-xl fixed left-0 top-0 z-30 hidden lg:flex"
      >
        {navContent(false)}
      </motion.aside>

      {/* ── Mobile sidebar overlay (< lg) ── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMobile}
              className="fixed inset-0 bg-black/40 z-40 lg:hidden"
              data-testid="sidebar-mobile-backdrop"
            />
            {/* Drawer */}
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-0 top-0 h-screen w-72 z-50 flex flex-col border-r border-border bg-background lg:hidden"
              data-testid="sidebar-mobile-drawer"
            >
              {navContent(true)}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
